/**
 * 岩点构造 —— 逐行对拍 climb-core/src/lib.rs:972-1238
 * hold_from_pixels / hold_from_ellipse / wall_dist_holds_in_crop / detect_in_region
 */
import { rgbToHsv, rgbToLab, hueDist } from "./color";
import { labelComponents, gatherStats } from "./conn";
import { open as morphOpen, dilate as morphDilate } from "./morph";
import { median3 } from "./wall";
import { HoldKind, type Hold, type Params } from "./types";

/** 任意像素集合 -> 岩点（颜色统计 + 分类）。SAM 掩码 / 椭圆 / 套索共用。对拍 lib.rs:972 */
export function holdFromPixels(
  img: Uint8ClampedArray,
  gw: number,
  own: Uint32Array,
  p: Params,
): Hold | null {
  const area = own.length;
  if (area < 8) return null;
  const hueBins = new Uint32Array(72);
  let colored = 0;
  let satSum = 0;
  let valSum = 0;
  let sumX = 0;
  let sumY = 0;
  const bx: [number, number, number, number] = [
    0xffffffff, 0xffffffff, 0, 0,
  ];
  for (const i of own) {
    const x = i % gw;
    const y = Math.floor(i / gw);
    const pi = i * 4;
    const [hh, ss, vv] = rgbToHsv(img[pi], img[pi + 1], img[pi + 2]);
    if (ss >= 0.22 && vv >= 0.1) {
      colored++;
      hueBins[(((Math.floor(hh / 5) % 72) + 72) % 72) | 0]++;
    }
    satSum += ss;
    valSum += vv;
    sumX += x;
    sumY += y;
    if (x < bx[0]) bx[0] = x;
    if (y < bx[1]) bx[1] = y;
    if (x + 1 > bx[2]) bx[2] = x + 1;
    if (y + 1 > bx[3]) bx[3] = y + 1;
  }
  let modeBin = 0;
  for (let i = 1; i < 72; i++) {
    if (hueBins[i] > hueBins[modeBin]) modeBin = i;
  }
  let win = 0;
  for (let d = -2; d <= 2; d++) {
    win += hueBins[(((modeBin + d) % 72) + 72) % 72];
  }
  const purity = colored > 0 ? win / colored : 0;
  const coloredFrac = colored / area;
  const satMean = satSum / area;
  const valMean = valSum / area;
  const kind =
    coloredFrac >= p.minColoredFrac && purity >= 0.45
      ? HoldKind.Colored
      : valMean < 0.25
        ? HoldKind.Black
        : valMean > 0.7 && satMean < 0.3
          ? HoldKind.White
          : HoldKind.Gray;
  // Lab 色度中心：众数色相 ±15° 内彩色像素；不足退回全部彩色像素
  const modeCenter = modeBin * 5 + 2.5;
  const ab: [number, number] = [0, 0];
  if (kind === HoldKind.Colored) {
    let as_ = 0,
      bs = 0,
      cw = 0;
    for (const i of own) {
      const pi = i * 4;
      const [hh, ss, vv] = rgbToHsv(img[pi], img[pi + 1], img[pi + 2]);
      if (ss >= 0.22 && vv >= 0.1 && hueDist(hh, modeCenter) <= 15) {
        const l = rgbToLab(img[pi], img[pi + 1], img[pi + 2]);
        as_ += l[1];
        bs += l[2];
        cw++;
      }
    }
    if (cw >= 6) {
      ab[0] = as_ / cw;
      ab[1] = bs / cw;
    }
  }
  return {
    id: -1, // Rust usize::MAX；入列前由 analyze 赋序号
    label: 0,
    ownPixels: own,
    area,
    bbox: bx,
    centroid: [sumX / area, sumY / area],
    hue: kind === HoldKind.Colored ? modeBin * 5 + 2.5 : 0,
    ab,
    sat: satMean,
    val: valMean,
    purity,
    kind,
    cluster: -1,
    lasso: false,
  };
}

/** 椭圆兜底：框内差分失败时用内切椭圆直接当掩码。对拍 lib.rs:1072 */
export function holdFromEllipse(
  img: Uint8ClampedArray,
  gw: number,
  gx0: number,
  gy0: number,
  cw: number,
  ch: number,
  p: Params,
): Hold | null {
  if (cw < 3 || ch < 3) return null;
  const ecx = (cw - 1) * 0.5;
  const ecy = (ch - 1) * 0.5;
  const rx = cw * 0.42;
  const ry = ch * 0.42;
  const own: number[] = [];
  for (let dy = 0; dy < ch; dy++) {
    const gy = gy0 + dy;
    for (let dx = 0; dx < cw; dx++) {
      const ex = (dx - ecx) / rx;
      const ey = (dy - ecy) / ry;
      if (ex * ex + ey * ey > 1) continue;
      own.push(gy * gw + gx0 + dx);
    }
  }
  return holdFromPixels(img, gw, Uint32Array.from(own), p);
}

/**
 * 裁剪窗口内墙面差分岩点检测（框内掩码提取 / 套索补测 / 超大块补测共用）。
 * region：与裁剪窗口等大的 0/255 掩码（undefined = 全窗口）。对拍 lib.rs:1124
 */
export function wallDistHoldsInCrop(
  img: Uint8ClampedArray,
  gw: number,
  gx0: number,
  gy0: number,
  cw: number,
  ch: number,
  region: Uint8Array | undefined,
  p: Params,
  minPx: number,
  maxPx: number,
): Hold[] {
  if (cw < 4 || ch < 4) return [];
  const nLoc = cw * ch;
  const lh = new Float32Array(nLoc);
  const ls = new Float32Array(nLoc);
  const lv = new Float32Array(nLoc);
  const llab = new Float32Array(nLoc * 3);
  const sample: number[] = [];
  for (let dy = 0; dy < ch; dy++) {
    const gy = gy0 + dy;
    for (let dx = 0; dx < cw; dx++) {
      const li = dy * cw + dx;
      if (region && region[li] === 0) continue;
      const gi = gy * gw + gx0 + dx;
      const pi = gi * 4;
      const [hh, ss, vv] = rgbToHsv(img[pi], img[pi + 1], img[pi + 2]);
      lh[li] = hh;
      ls[li] = ss;
      lv[li] = vv;
      const lab = rgbToLab(img[pi], img[pi + 1], img[pi + 2]);
      llab[li * 3] = lab[0];
      llab[li * 3 + 1] = lab[1];
      llab[li * 3 + 2] = lab[2];
      if (ss < 0.2) sample.push(li);
    }
  }
  // 局部墙面参考
  let med: [number, number, number];
  let sigma: number;
  if (sample.length >= 30) {
    const cl = sample.map((i) => llab[i * 3]);
    const ca = sample.map((i) => llab[i * 3 + 1]);
    const cb = sample.map((i) => llab[i * 3 + 2]);
    const ml = median3(cl);
    const ma = median3(ca);
    const mb = median3(cb);
    const ds = sample.map((i) =>
      Math.sqrt(
        (llab[i * 3] - ml) ** 2 +
          (llab[i * 3 + 1] - ma) ** 2 +
          (llab[i * 3 + 2] - mb) ** 2,
      ),
    );
    med = [ml, ma, mb];
    sigma = Math.min(Math.max(1.4826 * median3(ds), 2.5), 14.0);
  } else {
    med = [70, 0, 0];
    sigma = 6.0;
  }
  const t = Math.min(Math.max(p.wallK * sigma * 0.85, 6.0), 30.0);

  let mask: Uint8Array = new Uint8Array(nLoc);
  for (let li = 0; li < nLoc; li++) {
    if (region && region[li] === 0) continue;
    const d = Math.sqrt(
      (llab[li * 3] - med[0]) ** 2 +
        (llab[li * 3 + 1] - med[1]) ** 2 +
        (llab[li * 3 + 2] - med[2]) ** 2,
    );
    if (d > t) mask[li] = 255;
  }
  mask = morphOpen(mask, cw, ch, 1);
  mask = morphDilate(mask, cw, ch, 2);

  // 边缘 1px 裁掉（窗口边界截断的假轮廓）
  for (let dx = 0; dx < cw; dx++) {
    mask[dx] = 0;
    mask[(ch - 1) * cw + dx] = 0;
  }
  for (let dy = 0; dy < ch; dy++) {
    mask[dy * cw] = 0;
    mask[dy * cw + cw - 1] = 0;
  }

  const { labels, count } = labelComponents(mask, cw, ch);
  const stats = gatherStats(labels, lh, ls, lv, cw, ch, count);

  const out: Hold[] = [];
  for (const s of stats) {
    if (s.area === 0 || s.area < minPx || s.area > maxPx) continue;
    if (s.fill() < Math.max(p.minFillRatio - 0.05, 0.15)) continue;
    const own: number[] = [];
    for (let dy = 0; dy < ch; dy++) {
      const base = dy * cw;
      for (let dx = 0; dx < cw; dx++) {
        if (labels[base + dx] === s.label) {
          own.push((gy0 + dy) * gw + (gx0 + dx));
        }
      }
    }
    const h = holdFromPixels(img, gw, Uint32Array.from(own), p);
    if (h) out.push(h);
  }
  return out;
}

/**
 * 套索区域内补测（对拍 detect_in_region lib.rs:1242）。
 * region：与分析图等大的 0/255 掩码。
 */
export function detectInRegion(
  img: Uint8ClampedArray,
  w: number,
  h: number,
  region: Uint8Array,
  p: Params,
): Hold[] {
  const n = w * h;
  if (region.length !== n) throw new Error("region size mismatch");
  let x0 = w;
  let y0 = h;
  let x1 = 0;
  let y1 = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (region[y * w + x] !== 0) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x + 1 > x1) x1 = x + 1;
        if (y + 1 > y1) y1 = y + 1;
      }
    }
  }
  if (x1 <= x0 || y1 <= y0) return [];
  const cw = x1 - x0;
  const ch = y1 - y0;
  // 先裁出窗口局部子掩码（上游注释：曾因直接透传导致套索永远检测为空）
  const cropRegion = new Uint8Array(cw * ch);
  for (let dy = 0; dy < ch; dy++) {
    const srcRow = (y0 + dy) * w + x0;
    const dstRow = dy * cw;
    for (let dx = 0; dx < cw; dx++) {
      cropRegion[dstRow + dx] = region[srcRow + dx];
    }
  }
  const minPx = Math.trunc(Math.max(p.minAreaFrac * 0.35 * n, 30));
  const maxPx = Math.trunc(p.maxColoredAreaFrac * 2.5 * n);
  const out = wallDistHoldsInCrop(img, w, x0, y0, cw, ch, cropRegion, p, minPx, maxPx);
  for (const h2 of out) h2.lasso = true;
  return out;
}
